import "../../styles/adventure-scene.css";
import LlAudioSearchCombo from "./ll-audio-search-combo.marko";
import LlPillSearch from "./ll-pill-search.marko";
import { pickReaderTaglineText } from "../../lib/reader-tagline.js";
import { heroCoverAttrsFromPublicPath } from "../../lib/responsive-story-images.js";
import GrammarRecommendationsSection from "./grammar-recommendations-section.marko";
import LlReaderPdfBtn from "./ll-reader-pdf-btn.marko";

static function dialogHeroTextEmbedFields(sceneImagePath) {
  const path = String(sceneImagePath || "").trim();
  if (!path) {
    return { dialogHeroProgressiveJson: null, dialogHeroPreviewSrc: null };
  }
  const built = heroCoverAttrsFromPublicPath(path);
  return {
    dialogHeroProgressiveJson: built.progressiveJson || null,
    dialogHeroPreviewSrc: built.src || null,
  };
}

<if=!input.data.dialog>
  <div class="container">
    <div class="empty">
      <div class="empty__icon">💬</div>
      <p class="empty__title">${input.data.notFoundTitle || ""}</p>
      <a href=input.data.basePath class="btn btn--outline" style="margin-top:var(--space-md)">← ${input.data.backLabel || ""}</a>
    </div>
  </div>
</if>
<else>
  <div class="container">
    <div class="reader-wrap" id="ll-reader-wrap">
      <main
        class="reader-main ll-text-content"
        id="ll-text-content-root"
        data-content-kind="dialog"
        data-content-id=(input.data.dialog.contentId || "")
      >
        <div class="ll-reader-top-bar">
          <a class="ll-reader-back-link" href=input.data.basePath>← ${input.data.allLabel || ""}</a>
          <if=input.data.pdfUrl>
            <LlReaderPdfBtn
              pdfUrl=input.data.pdfUrl
              title=input.data.downloadPdfLabel
              ariaLabel=input.data.downloadPdfAriaLabel
              label=input.data.pdfAbbrevLabel
            />
          </if>
        </div>

        <if=(input.data.dialog.learnerTitle || input.data.dialog.translatedTitle)>
          <p class="title-aid title-aid--detail">${input.data.dialog.learnerTitle || input.data.dialog.translatedTitle}</p>
        </if>
        <div class="reader-title-row">
          <h1 class="reader-title">${input.data.dialog.targetTitle || input.data.dialog.title}</h1>
        </div>

        <if=(input.data.dialog.level || pickReaderTaglineText(input.data.dialog))>
          <div class="reader-desc-line">
            <if=input.data.dialog.level>
              <span class=["badge", `badge--${(input.data.dialog.level || '').toLowerCase()}`, "reader-desc-level"]>${input.data.dialog.level}</span>
            </if>
            <if=pickReaderTaglineText(input.data.dialog)>
              <p class="reader-desc reader-desc--plain reader-desc--inline">${pickReaderTaglineText(input.data.dialog)}</p>
            </if>
          </div>
        </if>

        <if=input.data.dialogSceneImagePath>
          <for|hero| of=[heroCoverAttrsFromPublicPath(input.data.dialogSceneImagePath)]>
            <div class="ll-story-hero ll-story-hero--scene" id="ll-dialog-scene-hero">
              <div class="ll-story-hero__frame">
                <img
                  class="ll-story-hero__img"
                  src=hero.src
                  data-ll-hero-progressive=hero.progressiveJson
                  alt=(input.data.dialog.targetTitle || input.data.dialog.title || "")
                  width=hero.width
                  height=hero.height
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  onError="this.style.display='none';this.parentElement.innerHTML='<div class=&quot;ll-story-hero__fallback&quot;>💬</div>'"
                >
              </div>
            </div>
          </for>
        </if>

        <div class="ll-reader-audio-dock" id="ll-reader-audio-dock">
          <LlAudioSearchCombo
            showSearch=false
            audioSrc=(input.data.dialogAudioSrc || "")
            audioDurationHint=(input.data.dialogAudioDurationHint != null ? input.data.dialogAudioDurationHint : "")
            audioDebug=(input.data.dialogAudioDebugMessage || "")
            audioTitle=(input.data.audioBadgeLabel || "")
            audioAriaLabel=(input.data.audioBadgeLabel || "")
            scrollFollowTitle=(input.data.scrollFollowTitle || "")
            scrollFollowAriaLabel=(input.data.scrollFollowAriaLabel || "")
          />
        </div>

        <div class="ll-meter-warning" id="ll-meter-warning" style="display:none">
          <span class="ll-meter-warning__text">${input.data.premiumGate?.labels?.meter?.warningBanner || ""}</span>
          <a
            class="ll-meter-warning__cta js-ll-purchase-cta"
            href="#"
            data-ll-analytics="0"
            data-ll-product=(input.data.premiumGate?.paywallPlans?.lern?.yearly?.plan?.product)
            data-ll-euros=(input.data.premiumGate?.paywallPlans?.lern?.yearly?.plan?.euros)
            data-ll-period=(input.data.premiumGate?.paywallPlans?.lern?.yearly?.plan?.period)
            data-ll-tier=(input.data.premiumGate?.paywallPlans?.lern?.yearly?.plan?.tier)
            data-ll-audience="lern"
            data-ll-pricing-tier-id=(input.data.premiumGate?.paywallPlans?.tierId)
          >${input.data.premiumGate?.labels?.meter?.ctaGuest || input.data.premiumGate?.labels?.meter?.cta || ""}</a>
        </div>

        <div id="ll-meter-inline-host"></div>

        <div class="dialog-lines reader-text" id="ll-text-root" data-ll-text-root>
          <for|row| of=(input.data.dialogRows || [])>
            <if=(row.kind === "narration")>
              <p class="dialog-narration">${row.text}</p>
            </if>
            <else>
              <div class=["dialog-line", row.align === "right" ? "dialog-line--pull-right" : "dialog-line--pull-left"]>
                <if=(row.speaker)><span class="dialog-line__name">${row.speaker}</span></if>
                <if=(row.textHtml)>
                  <div class="dialog-line__text">$!{row.textHtml}</div>
                </if>
                <else>
                  <div class="dialog-line__text">${row.text}</div>
                </else>
              </div>
            </else>
          </for>
        </div>

        <if=input.data.rawModules>
          <section class="ll-exercises-unit" id="ll-exercises-unit">
            <div class="ll-unit-title-row">
              <h2 class="ll-unit-title">${input.data.exerciseUnitTitle || ""}</h2>
            </div>
            <div class="ll-ex-list" id="ll-ex-list"></div>
            <div class="ll-ending-hint-host" id="ll-ending-hint-host" hidden></div>
            <div class="ll-exercise-lock" id="ll-exercise-lock" style="display:none">
              <div class="ll-exercise-lock__icon">🔒</div>
              <div class="ll-exercise-lock__list" id="ll-exercise-lock-list"></div>
              <a class="btn btn--premium btn--sm" id="ll-exercise-lock-cta" href=(input.data.premiumGate?.paymentLinks?.personal || "#")>${input.data.premiumGate?.labels?.exerciseLock?.unlockCta || ""}</a>
            </div>
          </section>
        </if>

        <if=input.data.grammarRecommendations>
          <GrammarRecommendationsSection rec=input.data.grammarRecommendations/>
        </if>

      </main>

      <div class="ll-word-infobox-backdrop" id="ll-word-infobox-backdrop" aria-hidden="true"></div>
      <aside class="reader-sidebar">
        <div class="reader-sidebar__search" id="ll-sidebar-search-section">
          <LlPillSearch
            id="ll-text-search-input"
            placeholder=(input.data.textTools?.labels?.dialogSearchPlaceholder || input.data.textTools?.labels?.searchPlaceholder || "")
            searchAriaLabel=(input.data.textTools?.labels?.textSearchAriaLabel || "")
            shellClass="reader-sidebar__search-wrap ll-wortschatz__search-wrap"
            inputClass="ll-search-input ll-wortschatz__search-input reader-sidebar__search-input"
          />
        </div>
        <div class="ll-word-infobox sidebar-section sidebar-section--word-details" id="ll-word-infobox">
          <p class="ll-word-infobox__empty" id="ll-word-infobox-empty">${input.data.wordInfoEmptyLabel || ""}</p>
        </div>
      </aside>
    </div>
  </div>

  <div class="ta-translate-tooltip" id="ll-translate-tooltip">
    <span class="ta-translate-tooltip__body">
      <span class="ta-translate-tooltip__translated" id="ll-tooltip-translated"></span>
      <span class="ta-translate-tooltip__hint" id="ll-tooltip-hint" hidden></span>
    </span>
    <span class="ta-translate-tooltip__more" aria-hidden="true">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 11.5v4.5"/><path d="M12 8h.01"/></svg>
    </span>
  </div>

  <html-script id="ll-text-data" type="application/json">
    $!{JSON.stringify({
      contentKind: "dialog",
      contentId: input.data.dialog.contentId || null,
      dialogSceneImagePath: input.data.dialogSceneImagePath || null,
      ...dialogHeroTextEmbedFields(input.data.dialogSceneImagePath),
      contentTitle: (typeof input.data.dialog.targetTitle === "string" ? input.data.dialog.targetTitle : (typeof input.data.dialog.title === "string" ? input.data.dialog.title : null)) || null,
      contentLevel: input.data.dialog.level || null,
      storyAudioSrc: input.data.dialogAudioSrc || null,
      storyAudioDurationHint: input.data.dialogAudioDurationHint || null,
      storyScript: input.data.storyScript || null,
      characters: input.data.dialogCharacters || input.data.dialog.characters || [],
      uiLang: input.data.textTools?.uiLang || null,
      targetLang: input.data.textTools?.targetLang || null,
      lexiconOccurrencesUrl: input.data.textTools?.lexiconOccurrencesUrl || "",
      lexiconTextUrl: input.data.textTools?.lexiconTextUrl || "",
      lexiconDataLocale: input.data.textTools?.lexiconDataLocale || "",
      lexiconSectionKey: input.data.textTools?.lexiconSectionKey || "",
      lexiconApiPath: input.data.textTools?.lexiconApiPath || "",
      lexiconApiOrigin: input.data.textTools?.lexiconApiOrigin || "",
      lexiconApiSpeakLang: input.data.textTools?.lexiconApiSpeakLang || "",
      contentApiOrigin: input.data.textTools?.contentApiOrigin || "",
      contentApiDevOrigin: input.data.textTools?.contentApiDevOrigin || "",
      paywallLanternSrc: input.data.textTools?.paywallLanternSrc || "",
      lexiconBundlePath: input.data.textTools?.lexiconBundlePath || "",
      labels: input.data.textTools?.labels || {},
      subtitleLines: input.data.dialogSubtitleLines || [],
      modules: input.data.rawModules || null,
      feedbackLabels: {
        correct: input.data.exerciseCorrectLabel || "",
        incorrect: input.data.exerciseIncorrectLabel || ""
      },
      exerciseLabels: {
        check: input.data.exerciseCheckLabel || "",
        next: input.data.exerciseNextLabel || "",
        reset: input.data.exerciseResetLabel || "",
        tryAgain: input.data.exerciseTryAgainLabel || "",
        showSolution: input.data.exerciseShowSolutionLabel || "",
        hideSolution: input.data.exerciseHideSolutionLabel || "",
        back: input.data.backLabel || "",
        forward: input.data.exerciseNextLabel || "",
        completeTaskFirst: input.data.exerciseCompleteTaskFirstLabel || "",
        points: input.data.exercisePointsLabel || "",
        stars: input.data.exerciseStarsLabel || "",
        summaryTitle: input.data.exerciseSummaryTitle || "",
        summarySubtitle: input.data.exerciseSummarySubtitle || "",
        reward: input.data.exerciseRewardLabel || "",
        setTitlePrefix: input.data.exerciseSetTitlePrefix || "",
        generalInstruction: input.data.exerciseGeneralInstruction || "",
        defaultInstruction: input.data.exerciseDefaultInstruction || "",
        selectOneInstruction: input.data.exerciseSelectOneInstruction || "",
        selectMultipleBlankInstruction: input.data.exerciseSelectMultipleBlankInstruction || "",
        orderVerticalInstruction: input.data.exerciseOrderVerticalInstruction || "",
        typeTitleSelectOne: input.data.exerciseTypeTitleSelectOne || "",
        typeTitleOrderVertical: input.data.exerciseTypeTitleOrderVertical || "",
        typeTitleSelectMultipleBlank: input.data.exerciseTypeTitleSelectMultipleBlank || "",
        typeTitleTypedCloze: input.data.exerciseTypeTitleTypedCloze || "",
        typeTitleVocabMatch: input.data.exerciseTypeTitleVocabMatch || "",
        typeTitleRule: input.data.exerciseTypeTitleRule || "",
        typeInstructionSelectOne: input.data.exerciseTypeInstructionSelectOne || "",
        typeInstructionOrderVertical: input.data.exerciseTypeInstructionOrderVertical || "",
        typeInstructionSelectMultipleBlank: input.data.exerciseTypeInstructionSelectMultipleBlank || "",
        typeInstructionVocabMatch: input.data.exerciseTypeInstructionVocabMatch || "",
        questionAriaPrefix: input.data.exerciseQuestionAriaPrefix || "",
        sectionMainLabel: input.data.exerciseSectionMainLabel || "",
        sectionEndingOneLabel: input.data.exerciseSectionEndingOneLabel || "",
        sectionEndingTwoLabel: input.data.exerciseSectionEndingTwoLabel || "",
        sectionMiddleLabel: input.data.exerciseSectionMiddleLabel || "",
        sectionEndLabel: input.data.exerciseSectionEndLabel || "",
        moduleDoneLabel: input.data.exerciseModuleDoneLabel || "",
        moduleCurrentLabel: input.data.exerciseModuleCurrentLabel || "",
        moduleOpenLabel: input.data.exerciseModuleOpenLabel || "",
        debugCardTitle: input.data.exerciseDebugCardTitle || "",
        debugCardMeta: input.data.exerciseDebugCardMeta || "",
        summaryUnitsLabel: input.data.exerciseSummaryUnitsLabel || "",
        summaryQuestionsLabel: input.data.exerciseSummaryQuestionsLabel || "",
        examModeLabel: input.data.exerciseExamModeLabel || "",
        practiceModeLearnLabel: input.data.exercisePracticeModeLearnLabel || "",
        practiceModeTestLabel: input.data.exercisePracticeModeTestLabel || "",
        practiceModeChooseAria: input.data.exercisePracticeModeChooseAria || "",
        practiceLearnCompleteLabel: input.data.exercisePracticeLearnCompleteLabel || "",
        practiceTestRetryAllLabel: input.data.exercisePracticeTestRetryAllLabel || "",
        practiceTestRetryWrongLabel: input.data.exercisePracticeTestRetryWrongLabel || "",
        practiceTestWrongHint: input.data.exercisePracticeTestWrongHint || "",
        summaryPerfectTitle: input.data.exerciseSummaryPerfectTitle || "",
        summaryPerfectSubtitle: input.data.exerciseSummaryPerfectSubtitle || "",
        summaryPartialTitle: input.data.exerciseSummaryPartialTitle || "",
        summaryPartialSubtitle: input.data.exerciseSummaryPartialSubtitle || "",
        summaryFailedTitle: input.data.exerciseSummaryFailedTitle || "",
        summaryFailedSubtitle: input.data.exerciseSummaryFailedSubtitle || ""
      },
      moduleKindLabels: input.data.moduleKindLabels || {}
      ,
      premiumGate: input.data.premiumGate || null,
      devMeterDebug: process.env.NODE_ENV !== "production",
      devListenTimestampEdit: process.env.NODE_ENV !== "production"
    })}
  </html-script>
  <html-script src="/js/grammar-recommendations-client.js"></html-script>
  <html-script src="/js/text-content-client.js"></html-script>

  <if=input.data.premiumGate>
    <html-script type="application/ld+json">
      $!{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": input.data.dialog.targetTitle || input.data.dialog.title || "",
        "description": pickReaderTaglineText(input.data.dialog) || input.data.dialog.translatedDescription || input.data.dialog.description || "",
        "isAccessibleForFree": !input.data.premiumGate.blocked,
        "hasPart": [
          {
            "@type": "WebPageElement",
            "isAccessibleForFree": true,
            "cssSelector": "#ll-text-root"
          },
          {
            "@type": "WebPageElement",
            "isAccessibleForFree": !(input.data.premiumGate.lockExercises),
            "cssSelector": "#ll-exercises-unit"
          }
        ]
      })}
    </html-script>
  </if>
</else>
